'use client';

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getUserById, createUser, updateUser, clearUserErrors } from '../../redux/actions/userActions';
import userService from '../../services/userService';

const CreateUserSchema = Yup.object().shape({
  email: Yup.string().email('Email invalide').required('Email requis'),
  password: Yup.string()
    .required('Mot de passe requis')
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial'
    ),
  firstName: Yup.string().required('Prénom requis'),
  lastName: Yup.string().required('Nom requis'),
  roleName: Yup.string().required('Rôle requis')
});

const UpdateUserSchema = Yup.object().shape({
  firstName: Yup.string().required('Prénom requis'),
  lastName: Yup.string().required('Nom requis'),
  roleName: Yup.string().required('Rôle requis'),
  emailVerified: Yup.boolean(),
  companyName: Yup.string().required("Nom de l'entreprise requis"),
  companySiret: Yup.string().required('SIRET requis'),
  companyAddress: Yup.string().required('Adresse requise'),
  companyPhone: Yup.string().required('Téléphone requis'),
  emergencyContact: Yup.string().nullable()
});

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector(state => state.users);
  const { user: currentUser } = useSelector(state => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('fiche');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('général');

  const [adminActionLoading, setAdminActionLoading] = useState(null);
  const [adminActionMessage, setAdminActionMessage] = useState('');
  const [adminActionError, setAdminActionError] = useState('');

  // Vacation local state
  const [vacations, setVacations] = useState([]);
  const [vacationForm, setVacationForm] = useState({ startDate: '', endDate: '', label: '' });
  const [editingVacation, setEditingVacation] = useState(null);
  const [vacationError, setVacationError] = useState('');
  const [vacationSuccess, setVacationSuccess] = useState('');
  const [vacationSaving, setVacationSaving] = useState(false);

  useEffect(() => {
    if (id) {
      setIsEditing(true);
      dispatch(getUserById(id));
    } else {
      setIsEditing(false);
    }
    return () => { dispatch(clearUserErrors()); };
  }, [dispatch, id]);

  useEffect(() => {
    if (user?.vacations) setVacations(user.vacations);
  }, [user]);

  const isSelfEditing = isEditing && currentUser?.id === parseInt(id);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isEditing) {
        await dispatch(updateUser(id, values));
      } else {
        await dispatch(createUser(values));
        navigate('/admin/users');
      }
    } catch (err) {
      console.error('Erreur lors de la soumission:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getInitialValues = () => {
    if (isEditing && user) {
      return {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roleName: user.role || 'user',
        emailVerified: user.emailVerified || false,
        companyName: user.companyName || '',
        companySiret: user.companySiret || '',
        companyAddress: user.companyAddress || '',
        companyPhone: user.companyPhone || '',
        emergencyContact: user.emergencyContact || ''
      };
    }
    return { email: '', password: '', firstName: '', lastName: '', roleName: 'user' };
  };

  // ── Vacation handlers ──
  const handleVacationSubmit = async (e) => {
    e.preventDefault();
    setVacationError('');
    setVacationSuccess('');
    if (!vacationForm.startDate || !vacationForm.endDate) {
      setVacationError('Les dates de début et de fin sont requises.');
      return;
    }
    if (new Date(vacationForm.startDate) > new Date(vacationForm.endDate)) {
      setVacationError('La date de début doit être antérieure à la date de fin.');
      return;
    }
    setVacationSaving(true);
    try {
      if (editingVacation) {
        const result = await userService.updateUserVacation(id, editingVacation.id, vacationForm);
        setVacations(prev => prev.map(v => v.id === editingVacation.id ? result.vacation : v));
        setVacationSuccess('Période modifiée avec succès.');
        setEditingVacation(null);
      } else {
        const result = await userService.createUserVacation(id, vacationForm);
        setVacations(prev => [...prev, result.vacation]);
        setVacationSuccess('Période ajoutée avec succès.');
      }
      setVacationForm({ startDate: '', endDate: '', label: '' });
      setTimeout(() => setVacationSuccess(''), 3000);
    } catch (err) {
      setVacationError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setVacationSaving(false);
    }
  };

  const handleEditVacation = (v) => {
    setEditingVacation(v);
    setVacationForm({ startDate: v.startDate, endDate: v.endDate, label: v.label || '' });
    setVacationError('');
    setVacationSuccess('');
  };

  const handleDeleteVacation = async (vacationId) => {
    if (!window.confirm('Supprimer cette période de vacances ?')) return;
    setVacationError('');
    try {
      await userService.deleteUserVacation(id, vacationId);
      setVacations(prev => prev.filter(v => v.id !== vacationId));
      setVacationSuccess('Période supprimée.');
      setTimeout(() => setVacationSuccess(''), 3000);
    } catch (err) {
      setVacationError(err.response?.data?.message || 'Une erreur est survenue.');
    }
  };

  const handleAdminAction = async (action, label) => {
    setAdminActionLoading(action);
    setAdminActionMessage('');
    setAdminActionError('');
    try {
      const res = action === 'reset'
        ? await userService.sendPasswordReset(id)
        : await userService.resendVerificationEmail(id);
      setAdminActionMessage(res.data?.message || `${label} envoyé`);
      setTimeout(() => setAdminActionMessage(''), 5000);
    } catch (err) {
      setAdminActionError(err.response?.data?.message || 'Une erreur est survenue.');
      setTimeout(() => setAdminActionError(''), 5000);
    } finally {
      setAdminActionLoading(null);
    }
  };

  const cancelEditVacation = () => {
    setEditingVacation(null);
    setVacationForm({ startDate: '', endDate: '', label: '' });
    setVacationError('');
  };

  if (isEditing && loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des informations utilisateur...</p>
      </div>
    );
  }

  if (isEditing && error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/admin/users')}>
          Retour à la liste
        </button>
      </div>
    );
  }

  if (isEditing && !user) {
    return (
      <div className="not-found-container">
        <i className="material-icons">error</i>
        <h2>Utilisateur non trouvé</h2>
        <p>L'utilisateur que vous recherchez n'existe pas ou a été supprimé.</p>
        <button className="btn btn-primary" onClick={() => navigate('/admin/users')}>
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="user-form-container">
      <div className="page-header">
        <div className="header-left">
          <button className="btn btn-icon" onClick={() => navigate('/admin/users')}>
            <i className="material-icons">arrow_back</i>
          </button>
          <h1>{isEditing ? `${user?.firstName} ${user?.lastName}` : 'Nouvel utilisateur'}</h1>
        </div>
      </div>

      {/* ── Create mode: simple form ── */}
      {!isEditing && (
        <div className="form-card">
          {error && <div className="alert alert-danger">{error}</div>}
          <Formik
            initialValues={getInitialValues()}
            validationSchema={CreateUserSchema}
            onSubmit={handleSubmit}
            validateOnMount
          >
            {({ isSubmitting, errors, touched, isValid, dirty }) => (
              <Form className="user-form">
                <div className="form-section">
                  <h2>Informations du compte</h2>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <Field
                      type="email" name="email" id="email"
                      className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                      placeholder="email@exemple.com"
                    />
                    <ErrorMessage name="email" component="div" className="invalid-feedback" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Mot de passe *</label>
                    <div className="password-field">
                      <Field
                        type={showPassword ? 'text' : 'password'}
                        name="password" id="password"
                        className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                        placeholder="Mot de passe"
                      />
                      <button type="button" className="password-toggle" onClick={() => setShowPassword(p => !p)}>
                        <i className="material-icons">{showPassword ? 'visibility_off' : 'visibility'}</i>
                      </button>
                    </div>
                    <ErrorMessage name="password" component="div" className="invalid-feedback" />
                    <small className="form-text text-muted">
                      Au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.
                    </small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">Prénom *</label>
                      <Field
                        type="text" name="firstName" id="firstName"
                        className={`form-control ${errors.firstName && touched.firstName ? 'is-invalid' : ''}`}
                        placeholder="Prénom"
                      />
                      <ErrorMessage name="firstName" component="div" className="invalid-feedback" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Nom *</label>
                      <Field
                        type="text" name="lastName" id="lastName"
                        className={`form-control ${errors.lastName && touched.lastName ? 'is-invalid' : ''}`}
                        placeholder="Nom"
                      />
                      <ErrorMessage name="lastName" component="div" className="invalid-feedback" />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="roleName">Rôle *</label>
                    <Field as="select" name="roleName" id="roleName"
                      className={`form-control ${errors.roleName && touched.roleName ? 'is-invalid' : ''}`}
                    >
                      <option value="user">Utilisateur</option>
                      <option value="admin">Administrateur</option>
                    </Field>
                    <ErrorMessage name="roleName" component="div" className="invalid-feedback" />
                  </div>
                </div>

                <div className="form-actions">
                  <Link to="/admin/users" className="btn btn-outline-secondary">Annuler</Link>
                  <button type="submit" className="btn btn-primary"
                    disabled={!isValid || !dirty || isSubmitting || loading}
                  >
                    {isSubmitting || loading ? 'Enregistrement...' : 'Créer l\'utilisateur'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {/* ── Edit mode ── */}
      {isEditing && (
        <>
          {/* Header commun fiche + édition */}
          <div className="uf-header">
            <div className="uf-header__identity">
              <div className="user-avatar large">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div>
                <h2 className="uf-header__name">{user?.firstName} {user?.lastName}</h2>
                <p className="uf-header__email">{user?.email}</p>
                <div className="uf-header__badges">
                  <span className={`role-badge ${user?.role === 'admin' ? 'admin' : 'user'}`}>
                    {user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                  </span>
                  {user?.emailVerified
                    ? <span className="uf-badge uf-badge--verified"><i className="material-icons">verified</i> Email vérifié</span>
                    : <span className="uf-badge uf-badge--unverified"><i className="material-icons">mail</i> Email non vérifié</span>
                  }
                </div>
              </div>
            </div>
            <div className="uf-header__actions">
              {!user?.emailVerified && (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handleAdminAction('verify', 'Email de vérification')}
                  disabled={adminActionLoading !== null}
                  title="Renvoyer l'email de vérification"
                >
                  <i className="material-icons">mark_email_unread</i>
                  {adminActionLoading === 'verify' ? 'Envoi...' : 'Renvoyer la vérification'}
                </button>
              )}
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleAdminAction('reset', 'Email de réinitialisation')}
                disabled={adminActionLoading !== null}
                title="Envoyer un lien de réinitialisation de mot de passe"
              >
                <i className="material-icons">lock_reset</i>
                {adminActionLoading === 'reset' ? 'Envoi...' : 'Réinitialiser le mot de passe'}
              </button>
              {viewMode === 'fiche'
                ? <button className="btn btn-primary" onClick={() => setViewMode('edit')}>
                    <i className="material-icons">edit</i>
                    Modifier le profil
                  </button>
                : <button className="btn btn-outline-secondary" onClick={() => setViewMode('fiche')}>
                    <i className="material-icons">person</i>
                    Voir la fiche
                  </button>
              }
            </div>
          </div>
          {adminActionMessage && (
            <div className="alert alert-success">{adminActionMessage}</div>
          )}
          {adminActionError && (
            <div className="alert alert-danger">{adminActionError}</div>
          )}

          {/* ── Vue fiche (lecture seule) ── */}
          {viewMode === 'fiche' && (
            <div className="uf-fiche">
              <div className="uf-grid">

                {/* Informations générales */}
                <div className="uf-card">
                  <div className="uf-card__title">
                    <i className="material-icons">person</i>
                    Informations générales
                  </div>
                  <div className="uf-fields">
                    <div className="uf-field">
                      <span className="uf-field__label">Prénom</span>
                      <span className="uf-field__value">{user?.firstName || '—'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">Nom</span>
                      <span className="uf-field__value">{user?.lastName || '—'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">Email</span>
                      <span className="uf-field__value">{user?.email || '—'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">Téléphone</span>
                      <span className="uf-field__value">{user?.companyPhone || '—'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">Rôle</span>
                      <span className="uf-field__value">{user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">Email vérifié</span>
                      <span className="uf-field__value">
                        {user?.emailVerified
                          ? <span className="uf-check uf-check--yes"><i className="material-icons">check_circle</i> Oui</span>
                          : <span className="uf-check uf-check--no"><i className="material-icons">cancel</i> Non</span>
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Entreprise */}
                <div className="uf-card">
                  <div className="uf-card__title">
                    <i className="material-icons">business</i>
                    Entreprise
                  </div>
                  <div className="uf-fields">
                    <div className="uf-field">
                      <span className="uf-field__label">Nom</span>
                      <span className="uf-field__value">{user?.companyName || '—'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">SIRET</span>
                      <span className="uf-field__value">{user?.companySiret || '—'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">Adresse</span>
                      <span className="uf-field__value uf-field__value--pre">{user?.companyAddress || '—'}</span>
                    </div>
                    <div className="uf-field">
                      <span className="uf-field__label">Contact d'urgence</span>
                      <span className="uf-field__value">{user?.emergencyContact || '—'}</span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Vacances — pleine largeur */}
              <div className="uf-card uf-card--full">
                <div className="uf-card__title">
                  <i className="material-icons">beach_access</i>
                  Congés &amp; absences
                  <span className="uf-card__count">{vacations.length}</span>
                </div>
                {vacations.length === 0 ? (
                  <p className="uf-empty">Aucune période d'absence définie.</p>
                ) : (
                  <div className="uf-vacation-list">
                    {vacations.map(v => (
                      <div key={v.id} className="uf-vacation-item">
                        <i className="material-icons">event</i>
                        <span className="uf-vacation-dates">
                          {formatDate(v.startDate)} → {formatDate(v.endDate)}
                        </span>
                        {v.label && <span className="uf-vacation-label">{v.label}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Vue édition (formulaire existant) ── */}
          {viewMode === 'edit' && (
            <>
          {/* Tab navigation */}
          <div className="profile-tabs">
            {['général', 'entreprise', 'vacances'].map(tab => (
              <button
                key={tab}
                type="button"
                className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {/* Formik form covers: compte, entreprise, contact tabs */}
          <Formik
            initialValues={getInitialValues()}
            validationSchema={UpdateUserSchema}
            onSubmit={handleSubmit}
            validateOnMount
            enableReinitialize
          >
            {({ isSubmitting, errors, touched, isValid, dirty }) => (
              <Form>
                {/* ── Tab Général ── */}
                <div style={{ display: activeTab === 'général' ? 'block' : 'none' }}>
                  <div className="form-card">
                    <div className="form-section">
                      <h2>Informations générales</h2>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="firstName">Prénom *</label>
                          <Field type="text" name="firstName" id="firstName"
                            className={`form-control ${errors.firstName && touched.firstName ? 'is-invalid' : ''}`}
                          />
                          <ErrorMessage name="firstName" component="div" className="invalid-feedback" />
                        </div>
                        <div className="form-group">
                          <label htmlFor="lastName">Nom *</label>
                          <Field type="text" name="lastName" id="lastName"
                            className={`form-control ${errors.lastName && touched.lastName ? 'is-invalid' : ''}`}
                          />
                          <ErrorMessage name="lastName" component="div" className="invalid-feedback" />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="roleName">Rôle *</label>
                        <Field as="select" name="roleName" id="roleName"
                          className={`form-control ${errors.roleName && touched.roleName ? 'is-invalid' : ''}`}
                          disabled={isSelfEditing}
                        >
                          <option value="user">Utilisateur</option>
                          <option value="admin">Administrateur</option>
                        </Field>
                        <ErrorMessage name="roleName" component="div" className="invalid-feedback" />
                        {isSelfEditing && (
                          <small className="form-text text-muted">Vous ne pouvez pas modifier votre propre rôle.</small>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="companyPhone">Téléphone *</label>
                        <Field type="tel" name="companyPhone" id="companyPhone"
                          className={`form-control ${errors.companyPhone && touched.companyPhone ? 'is-invalid' : ''}`}
                          placeholder="Numéro de téléphone"
                        />
                        <ErrorMessage name="companyPhone" component="div" className="invalid-feedback" />
                      </div>

                      <div className="form-group">
                        <div className="form-check">
                          <Field type="checkbox" name="emailVerified" id="emailVerified" className="form-check-input" />
                          <label htmlFor="emailVerified" className="form-check-label">Email vérifié</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Tab Entreprise ── */}
                <div style={{ display: activeTab === 'entreprise' ? 'block' : 'none' }}>
                  <div className="form-card">
                    <div className="form-section">
                      <h2>Informations entreprise</h2>

                      <div className="form-group">
                        <label htmlFor="companyName">Nom de l'entreprise *</label>
                        <Field type="text" name="companyName" id="companyName"
                          className={`form-control ${errors.companyName && touched.companyName ? 'is-invalid' : ''}`}
                          placeholder="Nom de l'entreprise"
                        />
                        <ErrorMessage name="companyName" component="div" className="invalid-feedback" />
                      </div>

                      <div className="form-group">
                        <label htmlFor="companySiret">SIRET *</label>
                        <Field type="text" name="companySiret" id="companySiret"
                          className={`form-control ${errors.companySiret && touched.companySiret ? 'is-invalid' : ''}`}
                          placeholder="14 chiffres"
                        />
                        <ErrorMessage name="companySiret" component="div" className="invalid-feedback" />
                      </div>

                      <div className="form-group">
                        <label htmlFor="companyAddress">Adresse *</label>
                        <Field as="textarea" name="companyAddress" id="companyAddress"
                          className={`form-control ${errors.companyAddress && touched.companyAddress ? 'is-invalid' : ''}`}
                          rows={3} placeholder="Adresse complète"
                        />
                        <ErrorMessage name="companyAddress" component="div" className="invalid-feedback" />
                      </div>

                      <div className="form-group">
                        <label htmlFor="emergencyContact">Contact d'urgence</label>
                        <Field type="text" name="emergencyContact" id="emergencyContact"
                          className="form-control"
                          placeholder="Nom, relation, numéro de téléphone..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save/Cancel for Formik tabs */}
                {activeTab !== 'vacances' && (
                  <div className="form-actions">
                    <Link to="/admin/users" className="btn btn-outline-secondary">Annuler</Link>
                    <button type="submit" className="btn btn-primary"
                      disabled={!isValid || !dirty || isSubmitting || loading}
                    >
                      {isSubmitting || loading ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                  </div>
                )}
              </Form>
            )}
          </Formik>

          {/* ── Tab Vacances (outside Formik) ── */}
          {activeTab === 'vacances' && (
            <div className="form-card">
              <h2>Périodes de vacances</h2>
              <p className="profile-section-desc">
                Gérez les périodes d'absence de cet utilisateur.
              </p>

              <form className="vacation-form" onSubmit={handleVacationSubmit}>
                <h3 className="vacation-form-title">
                  {editingVacation ? 'Modifier une période' : 'Ajouter une période'}
                </h3>

                <div className="vacation-form-grid">
                  <div className="form-group">
                    <label htmlFor="vac-start">Date de début</label>
                    <input
                      type="date" id="vac-start" className="form-control"
                      value={vacationForm.startDate}
                      onChange={e => setVacationForm(p => ({ ...p, startDate: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="vac-end">Date de fin</label>
                    <input
                      type="date" id="vac-end" className="form-control"
                      value={vacationForm.endDate}
                      onChange={e => setVacationForm(p => ({ ...p, endDate: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group vacation-label-field">
                    <label htmlFor="vac-label">Libellé (optionnel)</label>
                    <input
                      type="text" id="vac-label" className="form-control"
                      placeholder="Ex : Congés d'été, RTT..."
                      value={vacationForm.label}
                      onChange={e => setVacationForm(p => ({ ...p, label: e.target.value }))}
                    />
                  </div>
                </div>

                {vacationError && <div className="alert alert-danger">{vacationError}</div>}

                <div className="vacation-form-actions">
                  <button type="submit" className="btn btn-primary" disabled={vacationSaving}>
                    {vacationSaving ? 'Enregistrement...' : editingVacation ? 'Modifier' : 'Ajouter'}
                  </button>
                  {editingVacation && (
                    <button type="button" className="btn btn-secondary" onClick={cancelEditVacation}>
                      Annuler
                    </button>
                  )}
                </div>
              </form>

              {vacationSuccess && <div className="alert alert-success">{vacationSuccess}</div>}

              <div className="vacation-list">
                <h3 className="vacation-list-title">Périodes de vacances</h3>

                {vacations.length === 0 ? (
                  <div className="vacation-empty">
                    <i className="material-icons">beach_access</i>
                    <p>Aucune période de vacances définie.</p>
                  </div>
                ) : (
                  <ul className="vacation-items">
                    {vacations.map(v => (
                      <li key={v.id} className="vacation-item">
                        <div className="vacation-item-icon">
                          <i className="material-icons">event</i>
                        </div>
                        <div className="vacation-item-info">
                          <span className="vacation-item-dates">
                            {formatDate(v.startDate)} → {formatDate(v.endDate)}
                          </span>
                          {v.label && <span className="vacation-item-label">{v.label}</span>}
                        </div>
                        <div className="vacation-item-actions">
                          <button className="btn-icon" title="Modifier" type="button"
                            onClick={() => handleEditVacation(v)}
                          >
                            <i className="material-icons">edit</i>
                          </button>
                          <button className="btn-icon btn-icon-danger" title="Supprimer" type="button"
                            onClick={() => handleDeleteVacation(v.id)}
                            disabled={vacationSaving}
                          >
                            <i className="material-icons">delete</i>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default UserForm;
